import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { TrendingUp, Rocket, Users, Target, ShieldCheck, Zap, BarChart3, PieChart } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Crescimento para Revendedores IPTV", href: "/blog/guia-crescimento-revendedores-iptv" }
  ];

  const sections = [
    {
      title: "1. Saindo do Amadorismo: Visão de Escala",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Para muitos, a revenda de IPTV começa como uma forma de ganhar R$ 500 ou R$ 1.000 extras por mês. Mas para os revendedores de elite, esse é apenas o ponto de partida. O crescimento real acontece quando você para de vender "listas" e começa a vender "experiência e entretenimento". Escalar exige uma mudança de mentalidade: de vendedor de balcão para gestor de plataforma.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O crescimento sustentável no IPTV baseia-se em três pilares: aquisição de novos clientes em massa, retenção extrema da base atual e otimização de margens de lucro através de pacotes estratégicos.
          </p>
        </div>
      )
    },
    {
      title: "2. Estratégias de Expansão de Mercado",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Target className="w-6 h-6 text-electric" /> Segmentação de Nicho</h4>
              <p className="text-sm text-muted-foreground">Não tente vender para "todo mundo". Crie campanhas específicas para fãs de esportes, amantes de cinema ou famílias com crianças. Cada nicho exige uma linguagem e uma oferta diferente.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Users className="w-6 h-6 text-amber-500" /> Rede de Afiliados e Indicações</h4>
              <p className="text-sm text-muted-foreground">Transforme seus melhores clientes em pequenos parceiros. Ofereça mensalidades grátis ou comissões para cada indicação convertida. Isso cria um exército de vendas orgânico e fiel.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Otimização Financeira e de Processos",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Quanto mais você cresce, mais os pequenos erros financeiros aparecem. Para escalar para centenas ou milhares de clientes, você precisa de precisão:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Planos Trimestrais e Semestrais</h4>
              <p className="text-muted-foreground">Incentive o cliente a pagar por períodos maiores oferecendo descontos. Isso aumenta seu fluxo de caixa imediato e garante a retenção por mais tempo sem esforço de cobrança mensal.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Reinvestimento em Marketing</h4>
              <p className="text-muted-foreground">Separe pelo menos 20% do seu lucro para investir em tráfego pago (Google Ads, Meta Ads) ou na criação de um site profissional. Negócios que não investem em visibilidade param no tempo.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Analisando os Números do Sucesso",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Revendedores de elite acompanham métricas avançadas para guiar seu crescimento:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <BarChart3 className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">LTV (Lifetime Value)</h5>
                <p className="text-sm text-muted-foreground">Quanto dinheiro um único cliente deixa na sua mão durante todo o tempo que ele fica com você. Seu objetivo é aumentar esse tempo ao máximo.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <PieChart className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">CAC (Custo de Aquisição de Cliente)</h5>
                <p className="text-sm text-muted-foreground">Saiba exatamente quanto você gasta em anúncios ou tempo para trazer um cliente novo. Se o seu lucro na primeira venda for maior que o CAC, você pode escalar infinitamente.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia de Crescimento para Revendedores IPTV: O Caminho para a Escala"
        subtitle="Saia da renda extra e construa um império digital. Estratégias avançadas de marketing, gestão e finanças para revendedores de elite."
        heroImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Se você já passou da fase de aprender a usar o painel e já tem seus primeiros clientes satisfeitos, parabéns: você superou o maior filtro do mercado. Agora, o desafio é outro: como crescer sem perder a qualidade? Como atrair clientes de forma previsível e não depender apenas da sorte ou do acaso?",
          "Neste guia de crescimento acelerado, vamos mergulhar nas táticas que transformam pequenas revendas em operações de faturamento altíssimo. Vamos falar sobre tráfego pago, funis de vendas automáticos, gestão financeira profissional e como montar uma equipe de atendimento quando você não der mais conta sozinho.",
          "O mercado de IPTV movimenta milhões diariamente. Existe uma fatia gigante esperando por revendedores profissionais, éticos e que pensam grande. Se esse é o seu perfil, este manual é o seu roteiro para o próximo nível."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "Quando é a hora de contratar ajuda no suporte?",
            answer: "Quando o tempo gasto respondendo mensagens estiver impedindo você de pensar em novas estratégias de vendas. Geralmente entre 150 e 200 clientes ativos, já vale considerar um assistente part-time."
          },
          {
            question: "Vale a pena investir em site próprio?",
            answer: "Com certeza. Um site transmite confiança, permite que você faça anúncios de forma profissional e automatiza a geração de leads qualificados."
          }
        ]}
        benefits={[
          {
            title: "Escalabilidade Real",
            description: "Crie processos que permitem que seu negócio cresça sem aumentar seu trabalho na mesma proporção.",
            icon: <Rocket className="w-6 h-6" />
          },
          {
            title: "Domínio de Mercado",
            description: "Torne-se a referência na sua região ou nicho através de um posicionamento profissional.",
            icon: <TrendingUp className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Diagnóstico Atual",
            description: "Analise seus números atuais, sua taxa de retenção e identifique os gargalos que impedem seu crescimento."
          },
          {
            title: "Plano de Expansão",
            description: "Defina metas de curto e longo prazo e implemente as estratégias de marketing e automação necessárias."
          }
        ]}
        diferenciais={[
          {
            title: "Parceria de Sucesso P2 PRO",
            description: "Nós crescemos junto com você. Oferecemos as melhores condições para revendedores que buscam escala e volume.",
            icon: <ShieldCheck className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="🚀 Pronto para Escalar para 5 Dígitos Mensais?"
        ctaDescription="Não se limite ao básico. Utilize a tecnologia e o suporte da P2 PRO para construir a maior revenda da sua região. O topo é o seu lugar."
        ctaButtonText="Quero me Tornar um Revendedor de Sucesso"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-crescimento-revendedores-iptv")({
  head: () => ({
    meta: [
      { title: "Guia de Crescimento para Revendedores IPTV: O Caminho para a Escala" },
      { name: "description", content: "Aprenda estratégias avançadas para escalar sua revenda IPTV. Tráfego pago, funis de vendas, gestão financeira e táticas de expansão para revendedores profissionais." },
    ],
  }),
  component: ArticlePage,
});